import { translate, useLocale } from "../../localization";
import { useRef } from "react";
import { v4 as uuidv4 } from "uuid";
import API from "../../services/API";
import baseElementTemplate from "../../data/baseElement.json";
import { useContent } from "../../context/ContentContext";
import {
  getElementFileConfig,
  isElementImageFile,
} from "../../utility/elementFiles";
import { canUploadReplacementModel } from "../../utility/elementIdentity";

const cloneBaseElementTemplate = () => JSON.parse(JSON.stringify(baseElementTemplate));

const getImageDimensions = (file) =>
  new Promise((resolve, reject) => {
    const img = new Image();
    img.onload = () => {
      resolve({ width: img.width, height: img.height });
    };
    img.onerror = (err) => reject(err);
    const reader = new FileReader();
    reader.onload = (e) => {
      img.src = e.target.result;
    };
    reader.onerror = (err) => reject(err);
    reader.readAsDataURL(file);
  });

const mergeInitialElementData = (basePayload, initialElementData) =>
  initialElementData
    ? {
        ...basePayload,
        ...initialElementData,
        labelData: {
          ...basePayload.labelData,
          ...initialElementData.labelData,
        },
        visualizerData: {
          ...basePayload.visualizerData,
          ...initialElementData.visualizerData,
        },
        worldUnit: {
          ...basePayload.worldUnit,
          ...initialElementData.worldUnit,
        },
      }
    : basePayload;

export const useElementUploadWorkflow = ({
  initialElementId,
  initialElementData,
} = {}) => {
  useLocale();
  const { upsertMyElement, patchMyElement, trackElementBuild } = useContent();
  const attemptRef = useRef(new WeakMap());

  const createElementPayload = async ({ file, name, description, optimization }) => {
    const basePayload = cloneBaseElementTemplate();
    const newElementPayload = mergeInitialElementData(basePayload, initialElementData);

    if (
      initialElementId &&
      !canUploadReplacementModel(initialElementId, initialElementData)
    ) {
      throw new Error(translate("New model uploads are disabled for cloned elements."));
    }

    const elementId = initialElementId || newElementPayload.id || uuidv4();
    newElementPayload.id = elementId;
    newElementPayload.dataObjectID = initialElementId
      ? initialElementData.dataObjectID
      : elementId;

    if (name && name.trim()) {
      newElementPayload.labelData.displayName = name.trim();
    }
    if (description && description.trim()) {
      newElementPayload.labelData.description = description.trim();
    }
    newElementPayload.fileName = file.name;
    if (optimization) {
      newElementPayload.optimization = optimization;
    }

    const fileConfig = getElementFileConfig(file.name);
    if (fileConfig?.visualizerData) {
      Object.assign(newElementPayload.visualizerData, fileConfig.visualizerData);
    }
    if (fileConfig?.worldUnit) {
      Object.assign(newElementPayload.worldUnit, fileConfig.worldUnit);
    }

    if (isElementImageFile(file.name)) {
      try {
        const { width, height } = await getImageDimensions(file);
        if (width > 0) {
          newElementPayload.visualizerData.xValue = height / width;
        }
      } catch (err) {
        console.error("Failed to get image dimensions:", err);
      }
    }

    return newElementPayload;
  };

  const uploadSingleElement = async ({ file, name, description, optimization, onProgress }) => {
    // A retry of the same selected file keeps its identity and import token.
    // A different file starts a distinct import, including original-model replacement.
    if (!attemptRef.current.has(file)) {
      attemptRef.current.set(file, { file, requestId: uuidv4(), payload: await createElementPayload({ file, name, description, optimization }) });
    }
    const attempt = attemptRef.current.get(file);
    const newElementPayload = attempt.payload;
    const createdElement = {
      id: newElementPayload.id,
      dataObjectID: newElementPayload.dataObjectID,
      fileName: file.name,
      name: newElementPayload.labelData.displayName,
      description: newElementPayload.labelData.description,
      dateModified: new Date().toISOString(),
      status: {
        type: "UPLOADING",
        timestamp: new Date().toISOString(),
      },
    };

    try {
      if (!attempt.sourceUploaded) {
        onProgress?.("preparing");
        // The shared Element upload response owns metadata registration. Repeating
        // this intent refreshes its upload URL without creating another import.
        const response = await API.post("elements/", {
          ...newElementPayload, sourceUpload: { requestId: attempt.requestId },
        }, { requiresAuth: true });
        const { sourceUploadUrl, sourceUploadHeaders, importId } = response?.data?.data || {};
        if (!sourceUploadUrl || !importId || !sourceUploadHeaders || typeof sourceUploadHeaders !== "object" || Array.isArray(sourceUploadHeaders)) {
          throw new Error(translate("Could not prepare the source upload. Please retry."));
        }
        attempt.importId = importId;
        if (!attempt.elementSaved) {
          attempt.elementSaved = true;
          upsertMyElement(createdElement, { prepend: true });
        }
        onProgress?.("uploading");
        const uploaded = await fetch(sourceUploadUrl, {
          method: "PUT",
          body: file,
          headers: sourceUploadHeaders,
        });
        if (!uploaded.ok) throw new Error(translate("The source upload failed. Please retry."));
        attempt.sourceUploaded = true;
      }
      onProgress?.("building");
      if (!attempt.buildRequested) {
        await API.post("build-element", newElementPayload, { requiresAuth: true });
        attempt.buildRequested = true;
      }
      trackElementBuild(createdElement.id);
      onProgress?.("complete");
      return { ...createdElement, status: { ...createdElement.status, type: "BUILDING" }, metadataImport: { importId: attempt.importId, state: "queued" } };
    } catch (error) {
      if (attempt.sourceUploaded) patchMyElement(createdElement.id, {
        status: {
          type: "BUILDING_FAILED",
          timestamp: new Date().toISOString(),
          message: error?.message || translate("Upload failed"),
        },
      });
      // Metadata remains editable even if the independent build request fails.
      if (attempt.sourceUploaded) error.uploadedElement = { ...createdElement, metadataImport: { importId: attempt.importId, state: "queued" } };
      throw error;
    }
  };

  return {
    createElementPayload,
    uploadSingleElement,
  };
};

export default useElementUploadWorkflow;
